import type { PageValidator } from '@omnia/ui'
import { getLocalStorage, getLogger, Services, setLocalStorage } from '@omnia/ui'
import { Workflow, WorkflowResult } from '@omnia/ui/src/lib/workflow/workflow'
import { getOrder } from '@vista-digital/occ-domain'
import { TicketingSubPages } from '@/common/services/models/pages'
import { navigationService } from '@/common/services/navigation.service'
import { TicketingWorkflowState } from './ticketing-workflow-state'

const logger = getLogger('Workflow')

export class TicketingWorkflow extends Workflow<TicketingSubPages, TicketingWorkflowState> {
    public constructor() {
        super(TicketingSubPages)
        this.pages = ['SeatPicker', 'TicketingLogin', 'TicketPicker', 'OrderSummary', 'OrderPayment']

        this.stateStorageKey = 'ticketing-workflow-state'

        this.hydrateState()

        // push `Concession` on index 3 page if it configured on the `CMS`
        if (navigationService.pageExist('Ticketing', 'Concessions') && this.state.isConcessionsEnabled) {
            this.pages.splice(3, 0, 'Concessions')
        }

        // If the workflow state is outdated, reset it
        if (this.state.orderId && !getOrder()) {
            this.updateState(_ => new TicketingWorkflowState())
        }
    }

    public hydrateState = () => {
        const localState = getLocalStorage<TicketingWorkflowState>(this.stateStorageKey)
        this.state = localState ?? new TicketingWorkflowState()
    }

    public inExactWorkflow(page: keyof TicketingSubPages, pathParams: { [key: string]: string }): boolean {
        const showtimeId = pathParams?.['showtimeId']
        if (showtimeId && (showtimeId === this.state.showtimeId || this.state.showtimeId === undefined)) {
            return this.inWorkflow(page)
        }

        return false
    }

    public inWorkflow(page: keyof TicketingSubPages) {
        return page === 'Concessions'
            ? !!this.state.isConcessionsEnabled && super.inWorkflow(page)
            : super.inWorkflow(page)
    }

    public updateState(updateState: (state: TicketingWorkflowState) => TicketingWorkflowState) {
        this.state = updateState(this.state)
        setLocalStorage(this.stateStorageKey, this.state)
    }

    public navigateBackToWorkflow(): boolean {
        if (this.state.currentPage && this.state.showtimeId) {
            return this.goToPage(this.state.currentPage, { showtimeId: this.state.showtimeId })
        }

        return false
    }

    public initWorkflowPages(isConcessionsEnabled: boolean) {
        this.updateState(s => ({ ...s, isConcessionsEnabled }))

        if (!navigationService.pageExist('Ticketing', 'Concessions')) return
        if (this.pages.includes('Concessions')) return
        if (!isConcessionsEnabled) return

        this.pages.splice(3, 0, 'Concessions')
    }

    public override end(): void {
        super.end()
        this.updateState(_ => new TicketingWorkflowState())
    }

    public goBackToPage(page: keyof TicketingSubPages) {
        this.state.targetReturnPage = page
        setLocalStorage(this.stateStorageKey, this.state)

        Services.NavigationService.goBack()
    }

    // eslint-disable-next-line complexity -- TODO
    public async checkPage(
        page: keyof TicketingSubPages,
        validator: PageValidator<TicketingSubPages>,
        pathParams: { [key: string]: string }
    ): Promise<WorkflowResult> {
        logger.value.trace('checkPage')

        const pageIndex = this.pages.indexOf(page)
        const currentPageIndex = this.pages.indexOf(this.state.currentPage)

        logger.value.trace('checkPage, current page and page index are', { page, pageIndex })

        // If page is not part of the workflow, ignore all checks
        if (!this.inWorkflow(page)) {
            logger.value.debug('This page is not part of this workflow.', { page, pageIndex })
            return WorkflowResult.NotValid
        }

        // If state has no data and the page is not the first one, return invalid workflow
        if (this.State.currentPage === undefined && pageIndex > 0) {
            logger.value.debug('State has no data and page is not the first one.', { page, pageIndex })
            return WorkflowResult.NotValid
        }

        // Checks whether an earlier page in the workflow has been targeted to return to
        if (this.State.targetReturnPage) {
            const desiredBackPageIndex = this.pages.indexOf(this.State.targetReturnPage)
            if (pageIndex > desiredBackPageIndex) {
                logger.value.debug(
                    `Page skipped on return to target page (${this.State.targetReturnPage.toString()}) action.`,
                    { page, pageIndex }
                )
                return WorkflowResult.SkipBack
            } else {
                this.State.targetReturnPage = undefined
                setLocalStorage(this.stateStorageKey, this.state)
            }
        }

        // If we are going back in the workflow
        if (currentPageIndex > pageIndex) {
            if (validator.canBeSkippedOnBack && (await validator.canBeSkippedOnBack(this.state, pathParams))) {
                logger.value.debug('Page can be skipped on back action.', { page, pageIndex })
                return WorkflowResult.SkipBack
            }
        }

        // If we are going forward in the workflow
        if (currentPageIndex < pageIndex) {
            if (validator.canBeSkipped && (await validator.canBeSkipped(this.state, pathParams))) {
                logger.value.debug('Page can be skipped.', { page, pageIndex })
                return WorkflowResult.SkipForward
            }
        }

        if (await validator.validate(this.state, pathParams)) {
            // Update state to this page
            this.state.currentPage = page
            setLocalStorage(this.stateStorageKey, this.state)

            logger.value.info(`Workflow page ${page.toString()} is valid. Workflow state updated in local storage.`, {
                localStorageKey: this.stateStorageKey,
                state: this.state,
            })

            return WorkflowResult.Ok
        }

        logger.value.debug('Page is not valid.', { page, pageIndex })
        if (pageIndex === 0) {
            return WorkflowResult.NotValid
        } else return WorkflowResult.SkipBack
    }
}

export const ticketingWorkflow = new TicketingWorkflow()
